// Finding anchors, handles and segments of visible, unlocked paths and shapes under the pointer (world
// coordinates). A live shape answers with its expanded outline; editing it turns it into a path (D3).
import type { DisplayList, Item } from '../render/displayList';
import { geometry } from '../render/displayList';
import type { Mat2D, Subpath } from '../model/types';
import { inv } from '../geometry/mat';
import { mapSubpath } from '../commands/commands';
import { nearest } from '../geometry/pathOps';

export type AnchorRef = { id: string; sp: number; i: number };
export type HandleRef = AnchorRef & { which: 'in' | 'out' };

const editable = (it: Item) =>
  !it.hidden && !it.locked && (it.node.kind === 'path' || it.node.kind === 'shape');

/** The item's outline in world coordinates. */
export const worldSubpaths = (it: Item): Subpath[] =>
  geometry(it.node).subpaths.map((sp) => mapSubpath(sp, it.world));
/** World subpaths back to the item's own coordinates. */
export const toLocal = (it: Item, sps: Subpath[]): Subpath[] =>
  sps.map((sp) => mapSubpath(sp, inv(it.world)));
export const localDelta = (world: Mat2D, dx: number, dy: number): [number, number] => {
  const m = inv(world);
  return [m[0] * dx + m[2] * dy, m[1] * dx + m[3] * dy];
};

function candidates(list: DisplayList, x: number, y: number, tol: number): Item[] {
  return list.tree
    .search({ minX: x - tol, minY: y - tol, maxX: x + tol, maxY: y + tol })
    .filter(editable)
    .sort((a, b) => b.order - a.order);
}

/** The topmost anchor within `tol` (world units). */
export function hitAnchor(
  list: DisplayList,
  x: number,
  y: number,
  tol: number,
): (AnchorRef & { item: Item }) | null {
  for (const it of candidates(list, x, y, tol)) {
    const sps = worldSubpaths(it);
    for (let s = 0; s < sps.length; s++)
      for (let i = 0; i < sps[s]!.points.length; i++) {
        const p = sps[s]!.points[i]!;
        if (Math.abs(p.x - x) <= tol && Math.abs(p.y - y) <= tol) return { id: it.id, sp: s, i, item: it };
      }
  }
  return null;
}

/** A handle (in / out) of one of the given anchors within `tol`. */
export function hitHandle(
  list: DisplayList,
  anchors: AnchorRef[],
  x: number,
  y: number,
  tol: number,
): (HandleRef & { item: Item }) | null {
  for (const a of anchors) {
    const it = list.byId.get(a.id);
    if (!it) continue;
    const p = worldSubpaths(it)[a.sp]?.points[a.i];
    if (!p) continue;
    for (const which of ['in', 'out'] as const) {
      const hx = which === 'in' ? p.inX : p.outX,
        hy = which === 'in' ? p.inY : p.outY;
      if ((hx !== p.x || hy !== p.y) && Math.abs(hx - x) <= tol && Math.abs(hy - y) <= tol)
        return { ...a, which, item: it };
    }
  }
  return null;
}

/** The topmost segment within `tol`: which subpath, segment and t, and the point on it (world). */
export function hitSegment(list: DisplayList, x: number, y: number, tol: number) {
  for (const it of candidates(list, x, y, tol)) {
    const h = nearest(worldSubpaths(it), x, y);
    if (h && h.dist <= tol) return { item: it, ...h };
  }
  return null;
}
